import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import indiaCities from "../data/indiaCities.json";
import API from "../api/axios";

function ChevronIcon({ size = 10, flipped = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="currentColor"
      style={{ transform: flipped ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
    >
      <path d="M8 11L3 6h10z" />
    </svg>
  );
}

function CheckIcon({ size = 9 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 12 12" fill="none">
      <polyline points="1,6 4,9 11,3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function buildPillLabel(location) {
  if (location.mode === "all") return "All India";
  const parts = [];
  if (location.institutions.length > 0) parts.push(...location.institutions);
  Object.values(location.cities).forEach((cityList) => parts.push(...cityList));
  Object.keys(location.statesOnly).forEach((s) => parts.push(s));
  if (parts.length === 0) return "All India";
  if (parts.length === 1) return parts[0].length > 22 ? parts[0].slice(0, 20) + "…" : parts[0];
  return `${parts.length} locations`;
}

function Checkbox({ checked }) {
  return (
    <div
      className={`flex-shrink-0 w-4 h-4 rounded-md flex items-center justify-center transition-all duration-150 ${
        checked
          ? "bg-accent text-white"
          : "bg-transparent border border-zinc-300 dark:border-zinc-600"
      }`}
    >
      {checked && <CheckIcon />}
    </div>
  );
}

export default function LocationPicker({ location, setLocation }) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("institution");
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const dropRef = useRef(null);

  const [instQuery, setInstQuery] = useState("");
  const [instResults, setInstResults] = useState([]);
  const [instLoading, setInstLoading] = useState(false);
  const userInstitution = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"))?.institution || null;
    } catch { return null; }
  })();

  const [areaQuery, setAreaQuery] = useState("");
  const stateResults = useMemo(
    () => (activeTab === "area" ? Object.keys(indiaCities).sort() : []),
    [activeTab],
  );
  const [cityMap, setCityMap] = useState({});
  const [expandedState, setExpandedState] = useState(null);
  const scrollRef = useRef(null);

  const updatePos = useCallback(() => {
    if (btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setDropPos({
        top: r.bottom + 6,
        left: Math.max(8, Math.min(r.left + r.width / 2 - 150, window.innerWidth - 308)),
      });
    }
  }, []);

  function openDrop() {
    updatePos();
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    const onScrollResize = () => updatePos();
    window.addEventListener("scroll", onScrollResize, true);
    window.addEventListener("resize", onScrollResize);
    return () => {
      window.removeEventListener("scroll", onScrollResize, true);
      window.removeEventListener("resize", onScrollResize);
    };
  }, [open, updatePos]);

  useEffect(() => {
    function onOut(e) {
      if (
        dropRef.current && !dropRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setOpen(false);
    }
    document.addEventListener("mousedown", onOut);
    return () => document.removeEventListener("mousedown", onOut);
  }, []);

  const [prevInstQuery, setPrevInstQuery] = useState(instQuery);
  if (prevInstQuery !== instQuery) {
    setPrevInstQuery(instQuery);
    if (!instQuery.trim()) setInstResults([]);
  }

  useEffect(() => {
    if (!instQuery.trim()) return;
    const t = setTimeout(async () => {
      setInstLoading(true);
      try {
        const res = await API.get("/institutions/search", { params: { q: instQuery, limit: 8 } });
        setInstResults(res.data || []);
      } catch { setInstResults([]); }
      setInstLoading(false);
    }, 280);
    return () => clearTimeout(t);
  }, [instQuery]);

  const loadCitiesForState = useCallback(
    (state) => {
      if (cityMap[state]) return;
      setCityMap((prev) => ({ ...prev, [state]: indiaCities[state] || [] }));
    },
    [cityMap],
  );

  useEffect(() => {
    if (!areaQuery.trim()) return;
    const q = areaQuery.toLowerCase();
    stateResults.forEach((state) => {
      const cities = indiaCities[state] || [];
      if (cities.some((c) => c.toLowerCase().includes(q))) loadCitiesForState(state);
    });
  }, [areaQuery, stateResults, loadCitiesForState]);

  function toggleAllIndia() {
    setLocation({ mode: "all", institutions: [], cities: {}, statesOnly: {} });
  }

  function toggleInstitution(name) {
    setLocation((prev) => {
      if (prev.mode === "all") return { mode: "custom", institutions: [name], cities: {}, statesOnly: {} };
      const has = prev.institutions.includes(name);
      const next = has ? prev.institutions.filter((i) => i !== name) : [...prev.institutions, name];
      const isEmpty = next.length === 0 && Object.keys(prev.cities).length === 0 && Object.keys(prev.statesOnly).length === 0;
      return isEmpty ? { mode: "all", institutions: [], cities: {}, statesOnly: {} } : { ...prev, mode: "custom", institutions: next };
    });
  }

  function toggleCity(state, city) {
    setLocation((prev) => {
      if (prev.mode === "all") return { mode: "custom", institutions: [], cities: { [state]: [city] }, statesOnly: {} };
      const stateCities = prev.cities[state] || [];
      const has = stateCities.includes(city);
      const next = has ? stateCities.filter((c) => c !== city) : [...stateCities, city];
      const newCities = { ...prev.cities };
      if (next.length === 0) delete newCities[state]; else newCities[state] = next;
      const newStatesOnly = { ...prev.statesOnly };
      delete newStatesOnly[state];
      const isEmpty = prev.institutions.length === 0 && Object.keys(newCities).length === 0 && Object.keys(newStatesOnly).length === 0;
      return isEmpty
        ? { mode: "all", institutions: [], cities: {}, statesOnly: {} }
        : { ...prev, mode: "custom", cities: newCities, statesOnly: newStatesOnly };
    });
  }

  function toggleStateOnly(state) {
    setLocation((prev) => {
      if (prev.mode === "all") return { mode: "custom", institutions: [], cities: {}, statesOnly: { [state]: true } };
      const has = prev.statesOnly[state];
      const newStatesOnly = { ...prev.statesOnly };
      if (has) delete newStatesOnly[state]; else newStatesOnly[state] = true;
      const newCities = { ...prev.cities };
      if (has) delete newCities[state];
      const isEmpty = prev.institutions.length === 0 && Object.keys(newCities).length === 0 && Object.keys(newStatesOnly).length === 0;
      return isEmpty
        ? { mode: "all", institutions: [], cities: {}, statesOnly: {} }
        : { ...prev, mode: "custom", cities: newCities, statesOnly: newStatesOnly };
    });
  }

  const filteredStates = areaQuery.trim()
    ? stateResults.filter((s) => {
        const q = areaQuery.toLowerCase();
        if (s.toLowerCase().includes(q)) return true;
        return (indiaCities[s] || []).some((c) => c.toLowerCase().includes(q));
      })
    : stateResults;

  const getIsExpanded = (state) => {
    if (expandedState === state) return true;
    if (areaQuery.trim()) {
      const q = areaQuery.toLowerCase();
      if (state.toLowerCase().includes(q)) return false;
      return (indiaCities[state] || []).some((c) => c.toLowerCase().includes(q));
    }
    return false;
  };

  const isAllIndia = location.mode === "all";
  const pillLabel = buildPillLabel(location);
  const isActive = !isAllIndia;

  const dropdown =
    open &&
    createPortal(
      <div
        ref={dropRef}
        className="bg-surface border border-border rounded-2xl shadow-lg overflow-hidden z-[99999]"
        style={{
          position: "fixed",
          top: dropPos.top,
          left: dropPos.left,
          width: "300px",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          animation: "locPickerIn 0.18s ease-out",
        }}
      >
        <style>{`
          @keyframes locPickerIn { from { opacity:0; transform:translateY(-4px) } to { opacity:1; transform:translateY(0) } }
          @keyframes spin { to { transform:rotate(360deg) } }
          .loc-scroll::-webkit-scrollbar { width:3px }
          .loc-scroll::-webkit-scrollbar-thumb { background:var(--scrollbar); border-radius:99px }
          .inst-name-scroll::-webkit-scrollbar { display:none }
        `}</style>

        {/* Header */}
        <div className="border-b border-border">
          <div className="flex items-center justify-between px-3.5 pt-3 pb-1.5">
            <span className="text-[0.65rem] font-semibold uppercase tracking-wider text-text-muted">
              Location
            </span>
            {!isAllIndia && (
              <button
                onClick={toggleAllIndia}
                className="text-[0.65rem] font-semibold text-red-500 hover:text-red-600 transition-colors cursor-pointer"
              >
                Clear all
              </button>
            )}
          </div>

          {/* All India */}
          <div
            onClick={toggleAllIndia}
            className={`flex items-center gap-2.5 px-3.5 py-2 cursor-pointer transition-colors rounded-lg mx-2 mb-1 ${
              isAllIndia ? "bg-accent/10" : "hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
            }`}
          >
            <Checkbox checked={isAllIndia} />
            <span className={`text-[0.8rem] ${isAllIndia ? "font-semibold text-accent" : "font-medium text-text"}`}>
              All India
            </span>
          </div>

          {/* Tabs */}
          <div className="flex gap-0 px-2 mb-0">
            {[{ id: "institution", label: "Institution" }, { id: "area", label: "Area" }].map((t) => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`flex-1 py-2 text-[0.72rem] font-medium transition-all border-b-2 cursor-pointer ${
                  activeTab === t.id
                    ? "border-accent text-accent font-semibold"
                    : "border-transparent text-text-muted hover:text-text"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Search inputs */}
        {activeTab === "institution" && (
          <div className="px-3.5 pt-3 pb-1">
            {userInstitution && (
              <div className="mb-2">
                <div className="text-[0.6rem] font-semibold uppercase tracking-wider text-text-muted mb-1.5">
                  Your Institution
                </div>
                <div
                  onClick={() => toggleInstitution(userInstitution)}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                    location.institutions.includes(userInstitution)
                      ? "bg-accent/10"
                      : "hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
                  }`}
                >
                  <Checkbox checked={location.institutions.includes(userInstitution)} />
                  <span className={`text-[0.8rem] truncate ${
                    location.institutions.includes(userInstitution) ? "font-semibold text-accent" : "font-medium text-text"
                  }`}>
                    {userInstitution}
                  </span>
                </div>
                <div className="h-px bg-border my-2" />
              </div>
            )}
            <div className="relative">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                className="text-text-muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={instQuery}
                onChange={(e) => setInstQuery(e.target.value)}
                placeholder="Search institutions"
                className="w-full pl-8 pr-3 py-2 bg-zinc-100 dark:bg-zinc-800/60 rounded-full text-[0.78rem] text-text outline-none border border-border focus:border-accent transition-colors placeholder:text-text-muted"
              />
            </div>
          </div>
        )}

        {activeTab === "area" && (
          <div className="px-3.5 pt-3 pb-1">
            <div className="relative">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"
                className="text-text-muted pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                value={areaQuery}
                onChange={(e) => setAreaQuery(e.target.value)}
                placeholder="Search state or city"
                className="w-full pl-8 pr-3 py-2 bg-zinc-100 dark:bg-zinc-800/60 rounded-full text-[0.78rem] text-text outline-none border border-border focus:border-accent transition-colors placeholder:text-text-muted"
              />
            </div>
          </div>
        )}

        {/* Scrollable results */}
        <div ref={scrollRef} className="loc-scroll max-h-[280px] overflow-y-auto px-2 py-1.5">
          {activeTab === "institution" && (
            <div>
              {instLoading && (
                <div className="py-6 text-center">
                  <div className="w-4 h-4 border-2 border-accent/30 border-t-accent rounded-full animate-spin mx-auto" />
                </div>
              )}
              {!instLoading && instResults.map((inst) => {
                const checked = location.institutions.includes(inst.name);
                return (
                  <div
                    key={inst.name}
                    onClick={() => toggleInstitution(inst.name)}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                      checked ? "bg-accent/10" : "hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
                    }`}
                  >
                    <Checkbox checked={checked} />
                    <div className="min-w-0">
                      <div className={`text-[0.78rem] truncate ${checked ? "font-semibold text-accent" : "font-medium text-text"}`}>
                        {inst.name}
                      </div>
                      {inst.city && (
                        <div className="text-[0.62rem] text-text-muted">
                          {inst.city}{inst.state ? `, ${inst.state}` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {!instLoading && instQuery && instResults.length === 0 && (
                <div className="text-[0.72rem] text-text-muted py-3 px-1">No results found.</div>
              )}
            </div>
          )}

          {activeTab === "area" && (
            <div>
              {filteredStates.map((state) => {
                const stateChecked = !!location.statesOnly[state];
                const stateCities = location.cities[state] || [];
                const isExpanded = getIsExpanded(state);
                const loadedCities = cityMap[state] || [];
                const visibleCities = areaQuery.trim()
                  ? loadedCities.filter((c) => c.toLowerCase().includes(areaQuery.toLowerCase()))
                  : loadedCities;
                return (
                  <div key={state} className="mb-0.5">
                    <div className="flex items-center gap-1">
                      <div
                        onClick={() => toggleStateOnly(state)}
                        className={`flex-1 flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                          stateChecked ? "bg-accent/10" : "hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
                        }`}
                      >
                        <Checkbox checked={stateChecked} />
                        <span className={`flex-1 text-[0.8rem] ${stateChecked ? "font-semibold text-accent" : "font-medium text-text"}`}>
                          {state}
                        </span>
                        {stateCities.length > 0 && !stateChecked && (
                          <span className="text-[0.55rem] font-bold text-accent bg-accent/10 px-1.5 py-0.5 rounded-full">
                            {stateCities.length}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          if (expandedState === state) setExpandedState(null);
                          else {
                            setExpandedState(state);
                            loadCitiesForState(state);
                            requestAnimationFrame(() => {
                              if (scrollRef.current) scrollRef.current.scrollBy({ top: 80, behavior: "smooth" });
                            });
                          }
                        }}
                        className="p-1.5 text-text-muted hover:text-accent transition-colors cursor-pointer"
                        title="Select specific cities"
                      >
                        <ChevronIcon size={10} flipped={isExpanded} />
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="pl-9 mt-0.5 mb-1">
                        {visibleCities.length === 0 ? (
                          <div className="py-1.5 text-[0.7rem] text-text-muted">
                            {loadedCities.length === 0 ? "Loading cities..." : "No cities match."}
                          </div>
                        ) : (
                          visibleCities.map((city) => {
                            const cityChecked = stateCities.includes(city);
                            return (
                              <div
                                key={city}
                                onClick={() => toggleCity(state, city)}
                                className={`flex items-center gap-2.5 px-2.5 py-1.5 rounded-lg cursor-pointer transition-colors ${
                                  cityChecked ? "bg-accent/10" : "hover:bg-zinc-100 dark:hover:bg-zinc-800/60"
                                }`}
                              >
                                <Checkbox checked={cityChecked} />
                                <span className={`text-[0.75rem] ${cityChecked ? "font-semibold text-accent" : "text-text"}`}>
                                  {city}
                                </span>
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {filteredStates.length === 0 && (
                <div className="text-[0.72rem] text-text-muted py-3 px-1">No results found.</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isAllIndia && (
          <div className="px-3.5 py-2.5 border-t border-border bg-zinc-50 dark:bg-zinc-900/50">
            <span className="text-[0.65rem] font-semibold text-accent">{pillLabel} selected</span>
          </div>
        )}
      </div>,
      document.body,
    );

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => { if (open) setOpen(false); else openDrop(); }}
        className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-[0.8rem] font-medium cursor-pointer transition-colors border border-border hover:border-border-hover ${
          isActive ? "bg-zinc-200 dark:bg-zinc-700 text-text" : "bg-zinc-100 dark:bg-zinc-800/60 text-text-secondary"
        }`}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="10" r="3" />
          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
        </svg>
        <span
          className={`max-w-[160px] truncate ${isActive || open ? "font-semibold" : "font-medium"}`}
        >
          {pillLabel}
        </span>
        <ChevronIcon size={9} flipped={open} />
      </button>
      {dropdown}
    </>
  );
}
